import { type ComponentProps, createContext, useContext } from 'react';

import {
	Select as ArkSelect,
	type SelectRootProps as ArkSelectRootProps,
	type CollectionItem,
	Portal
} from '@ark-ui/react';
import { CaretDown } from '@phosphor-icons/react/dist/csr/CaretDown';
import { Check } from '@phosphor-icons/react/dist/csr/Check';

import type { Scale3 } from '../../lib/types';
import { cn } from '../../lib/utils';

type SelectRootProps<T extends CollectionItem = CollectionItem> = ArkSelectRootProps<T> & {
	size?: Scale3;
};

const SelectSizeContext = createContext<Scale3>('2');

function SelectRoot<T extends CollectionItem = CollectionItem>({
	className,
	size = '2',
	lazyMount = true,
	unmountOnExit = true,
	positioning,
	...props
}: SelectRootProps<T>) {
	return (
		<SelectSizeContext.Provider value={size}>
			<ArkSelect.Root
				className={cn('pt-select', className)}
				data-size={size}
				lazyMount={lazyMount}
				unmountOnExit={unmountOnExit}
				positioning={{
					sameWidth: true,
					fitViewport: true,
					...positioning
				}}
				{...props}
			/>
		</SelectSizeContext.Provider>
	);
}

function SelectLabel({ className, ...props }: ComponentProps<typeof ArkSelect.Label>) {
	return <ArkSelect.Label className={cn('pt-select-label', className)} {...props} />;
}

function SelectControl({ className, ...props }: ComponentProps<typeof ArkSelect.Control>) {
	return <ArkSelect.Control className={cn('pt-select-control', className)} {...props} />;
}

type SelectTriggerProps = ComponentProps<typeof ArkSelect.Trigger> & {
	variant?: 'classic' | 'surface' | 'soft' | 'outline' | 'ghost';
	placeholder?: string;
};

function SelectTrigger({
	className,
	variant = 'surface',
	placeholder,
	children,
	...props
}: SelectTriggerProps) {
	return (
		<ArkSelect.Trigger
			className={cn('pt-select-trigger', className)}
			data-variant={variant}
			{...props}
		>
			{children ?? (
				<>
					<ArkSelect.ValueText placeholder={placeholder} />
					<ArkSelect.Indicator className="pt-select-indicator">
						<CaretDown size={16} weight="bold" />
					</ArkSelect.Indicator>
				</>
			)}
		</ArkSelect.Trigger>
	);
}

type SelectContentProps = ComponentProps<typeof ArkSelect.Content> & {
	variant?: 'solid' | 'soft';
};

function SelectContent({ className, variant = 'solid', ...props }: SelectContentProps) {
	const size = useContext(SelectSizeContext);

	return (
		<Portal>
			<ArkSelect.Positioner className="pt-select-positioner">
				<ArkSelect.Content
					className={cn('pt-select-content', className)}
					data-variant={variant}
					data-size={size}
					{...props}
				/>
			</ArkSelect.Positioner>
		</Portal>
	);
}

function SelectItemGroup({ className, ...props }: ComponentProps<typeof ArkSelect.ItemGroup>) {
	return <ArkSelect.ItemGroup className={cn('pt-select-item-group', className)} {...props} />;
}

function SelectItemGroupLabel({
	className,
	...props
}: ComponentProps<typeof ArkSelect.ItemGroupLabel>) {
	return <ArkSelect.ItemGroupLabel className={cn('pt-select-group-label', className)} {...props} />;
}

function SelectItem({ className, ...props }: ComponentProps<typeof ArkSelect.Item>) {
	return <ArkSelect.Item className={cn('pt-select-item', className)} {...props} />;
}

function SelectItemText({ className, ...props }: ComponentProps<typeof ArkSelect.ItemText>) {
	return <ArkSelect.ItemText className={cn('pt-select-item-text', className)} {...props} />;
}

function SelectItemIndicator({
	className,
	...props
}: ComponentProps<typeof ArkSelect.ItemIndicator>) {
	return (
		<ArkSelect.ItemIndicator className={cn('pt-select-item-indicator', className)} {...props}>
			<Check size={14} weight="bold" />
		</ArkSelect.ItemIndicator>
	);
}

function SelectClearTrigger({
	className,
	...props
}: ComponentProps<typeof ArkSelect.ClearTrigger>) {
	return <ArkSelect.ClearTrigger className={cn('pt-select-clear', className)} {...props} />;
}

function SelectValueText({ className, ...props }: ComponentProps<typeof ArkSelect.ValueText>) {
	return <ArkSelect.ValueText className={cn('pt-select-value-text', className)} {...props} />;
}

function SelectIndicator({ className, ...props }: ComponentProps<typeof ArkSelect.Indicator>) {
	return (
		<ArkSelect.Indicator className={cn('pt-select-indicator', className)} {...props}>
			<CaretDown size={16} weight="bold" />
		</ArkSelect.Indicator>
	);
}

const Select = Object.assign(SelectRoot, {
	Root: SelectRoot,
	Label: SelectLabel,
	Control: SelectControl,
	Trigger: SelectTrigger,
	ValueText: SelectValueText,
	Indicator: SelectIndicator,
	ClearTrigger: SelectClearTrigger,
	Content: SelectContent,
	ItemGroup: SelectItemGroup,
	ItemGroupLabel: SelectItemGroupLabel,
	Item: SelectItem,
	ItemText: SelectItemText,
	ItemIndicator: SelectItemIndicator,
	HiddenSelect: ArkSelect.HiddenSelect
});

export { Select, type SelectRootProps, type SelectTriggerProps, type SelectContentProps };
